import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiBarChart2, FiInfo } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import AIChat from '../components/chat/AIChat';
import UsageDashboard from '../components/chat/UsageDashboard';

/**
 * AI Chat Demo Page
 *
 * This page demonstrates the complete AI chat integration with:
 * - Real-time streaming responses
 * - WebSocket message broadcasting
 * - Usage tracking and cost display
 * - Token counting
 * - Error handling
 */
const AIChatDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chat' | 'usage' | 'info'>('chat');

  // For demo purposes, use a test conversation ID
  // In production, this would come from URL params or props
  const conversationId = 'test-conversation-123';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center gap-3 mb-2">
              <BiBot className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">AI Chat Integration</h1>
            </div>
            <p className="text-gray-600">
              Production-ready AI chat with GPT-4, streaming responses, and usage tracking
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 -mb-px">
            <TabButton
              active={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
              icon={<FiMessageSquare className="w-4 h-4" />}
              label="Chat"
            />
            <TabButton
              active={activeTab === 'usage'}
              onClick={() => setActiveTab('usage')}
              icon={<FiBarChart2 className="w-4 h-4" />}
              label="Usage & Costs"
            />
            <TabButton
              active={activeTab === 'info'}
              onClick={() => setActiveTab('info')}
              icon={<FiInfo className="w-4 h-4" />}
              label="Info"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {activeTab === 'chat' && (
            <div className="h-[calc(100vh-300px)]">
              <AIChat conversationId={conversationId} />
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="p-6">
              <UsageDashboard conversationId={conversationId} />
            </div>
          )}

          {activeTab === 'info' && (
            <div className="p-6 space-y-6">
              <InfoSection />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors relative
      ${active
        ? 'text-blue-600 bg-white'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }
    `}
  >
    {icon}
    {label}
    {active && (
      <motion.div
        layoutId="activeTab"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
      />
    )}
  </button>
);

const InfoSection: React.FC = () => (
  <div className="prose max-w-none">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">AI Chat Integration Features</h2>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <h3 className="text-lg font-semibold text-blue-900 mb-2">Status</h3>
      <p className="text-blue-800">
        ✅ Backend: Complete and ready (requires OpenAI API key)
        <br />
        ✅ Frontend: Complete with all features implemented
      </p>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Backend Services (Implemented)</h3>
    <ul className="space-y-2 text-gray-700">
      <li>✅ <strong>OpenAI Chat Service</strong> - GPT-4 integration with streaming support</li>
      <li>✅ <strong>Conversation Context Manager</strong> - Smart history loading with Redis caching</li>
      <li>✅ <strong>Usage Tracker</strong> - Accurate cost calculation and analytics</li>
      <li>✅ <strong>Chat API Endpoints</strong> - Standard and streaming endpoints</li>
    </ul>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Frontend Components (Implemented)</h3>
    <ul className="space-y-2 text-gray-700">
      <li>✅ <strong>AIChat Component</strong> - Full chat UI with streaming display</li>
      <li>✅ <strong>UsageDashboard</strong> - Cost and token analytics</li>
      <li>✅ <strong>useAIChat Hook</strong> - React hook for AI chat functionality</li>
      <li>✅ <strong>chatService</strong> - API client for chat endpoints</li>
    </ul>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Features</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <FeatureCard
        title="Real-time Streaming"
        description="AI responses stream in real-time with Server-Sent Events (SSE)"
      />
      <FeatureCard
        title="WebSocket Broadcasting"
        description="Messages broadcast to all connected clients in real-time"
      />
      <FeatureCard
        title="Usage Tracking"
        description="Automatic token counting and cost calculation for every request"
      />
      <FeatureCard
        title="Smart Context"
        description="Intelligent conversation history management with Redis caching"
      />
      <FeatureCard
        title="Cost Estimation"
        description="Real-time cost estimates as you type messages"
      />
      <FeatureCard
        title="Error Handling"
        description="Graceful handling of quota limits, rate limits, and service outages"
      />
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">API Endpoints</h3>
    <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm space-y-2">
      <div><span className="text-green-400">POST</span> /api/chat/:conversationId</div>
      <div className="text-gray-400 ml-4">Send message and get AI response</div>

      <div className="mt-2"><span className="text-blue-400">POST</span> /api/chat/:conversationId/stream</div>
      <div className="text-gray-400 ml-4">Send message and stream AI response (SSE)</div>

      <div className="mt-2"><span className="text-yellow-400">GET</span> /api/health</div>
      <div className="text-gray-400 ml-4">Check OpenAI service status</div>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Usage Example</h3>
    <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
      <pre className="overflow-x-auto">{`import AIChat from './components/chat/AIChat';

function MyConversation() {
  const conversationId = 'conv-123';

  return (
    <div className="h-screen">
      <AIChat conversationId={conversationId} />
    </div>
  );
}`}</pre>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Next Steps</h3>
    <ol className="space-y-2 text-gray-700 list-decimal list-inside">
      <li>Add OpenAI API key to environment variables</li>
      <li>Restart backend: <code className="bg-gray-100 px-2 py-1 rounded">docker-compose up -d backend</code></li>
      <li>Verify health check shows OpenAI as healthy</li>
      <li>Start chatting with real AI!</li>
    </ol>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
      <h4 className="font-semibold text-yellow-900 mb-2">Admin Note</h4>
      <p className="text-yellow-800 text-sm">
        To enable AI chat, add <code className="bg-yellow-100 px-2 py-1 rounded">OPENAI_API_KEY</code> to your
        docker-compose.yml environment variables. See <code>docs/Planning/API_Keys_Required.md</code> for instructions.
      </p>
    </div>
  </div>
);

interface FeatureCardProps {
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

export default AIChatDemo;
